import { useFormContext } from 'react-hook-form'
import { useTranslation, Trans } from 'react-i18next'
import { useState } from 'react'
import type { FileWithPath } from 'react-dropzone'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'

import { EducationHostedFormModel, EducationHostedFormSteps } from '../EducationHostedForm.types'
import { useHasCurrentPageErrors } from '../hooks/useHasCurrentPageErrors'
import { useNavigateForm } from '../hooks/useNavigateForm'

import { CAPTCHA_ACTIONS } from '@percent/utility'
import { Step } from '@percent/education-hosted-form/ui/Step/Step'
import { FormField, Dropzone, Badge, Spacer, Flex, Typography, Alert } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { FormState } from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { useMutation } from '@percent/education-hosted-form/hooks/useMutation'
import styles from './DocumentUpload.module.scss'
import { APIErrorResponse } from '@percent/education-hosted-form/types/api.types'
import { isInvalidCaptchaTokenError } from '@percent/education-hosted-form/utils/isInvalidCaptchaTokenError'

const DEFAULT_DOCUMENT_TYPE = 'agent_verification'

const STAFF_DOCUMENTS = [
  { key: 'documentUpload.staff.teacherLicense', recommended: true },
  { key: 'documentUpload.staff.payStub', recommended: true },
  { key: 'documentUpload.staff.teacherIdCard', recommended: false },
  { key: 'documentUpload.staff.facultyId', recommended: false },
  { key: 'documentUpload.staff.employmentContract', recommended: false },
  { key: 'documentUpload.staff.employmentCertificate', recommended: false }
] as const
const STUDENT_DOCUMENTS = [
  { key: 'documentUpload.typeOfDocument.enrolmentCertificate', recommended: true },
  { key: 'documentUpload.typeOfDocument.unofficialTranscript', recommended: false },
  { key: 'documentUpload.typeOfDocument.classSchedule', recommended: false },
  { key: 'documentUpload.typeOfDocument.schoolId', recommended: false }
] as const

export function DocumentUpload() {
  const { t } = useTranslation()
  const { progress, setFormState, partnerConfig } = useEducationHostedForm()
  const { programId } = useParams<{ programId: string }>()

  const eduHostedFormType = partnerConfig?.configuration.education?.eduHostedFormType
  const descriptionKey =
    eduHostedFormType === 'staff' ? 'documentUpload.staff.description' : 'documentUpload.description'
  const documentTypes = eduHostedFormType === 'staff' ? STAFF_DOCUMENTS : STUDENT_DOCUMENTS

  const partnerPrivacyPolicyUrl = partnerConfig?.configuration.education?.partnerPrivacyPolicyUrl
  const partnerName = partnerConfig?.partner.name

  const partnerPrivacyNotice = partnerPrivacyPolicyUrl && (
    <Trans
      i18nKey="documentUpload.partnerPrivacyNotice"
      values={{ partnerName }}
      components={{
        partnerPrivacyLink: (
          <a className={styles.link} target="_blank" href={partnerPrivacyPolicyUrl} rel="noopener noreferrer">
            _
          </a>
        )
      }}
    />
  )

  const [files, setFiles] = useState<FileWithPath[]>([])
  const [serverError, setServerError] = useState<string | null>(null)
  // Separate from serverError: a failed captcha is not a problem with the file, so it
  // must not render inside the dropzone.
  const [mustReverifyCaptcha, setMustReverifyCaptcha] = useState(false)
  const { handleNextStep } = useNavigateForm()

  const { setValue } = useFormContext<EducationHostedFormModel>()
  const { errors, getStatus } = useHasCurrentPageErrors([{ fieldKey: 'validationSubmissionDocumentId' }])

  const { mutate: uploadDocument, isPending } = useMutation<{ id: string }, Error, FormData>(
    async (client, formData, requestConfig) => {
      const response = await client.post('v1/validation-submission-documents', formData, {
        ...requestConfig,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...requestConfig?.headers
        }
      })

      return response.data
    },
    {
      captchaAction: CAPTCHA_ACTIONS.validationSubmissionDocument,
      onError: error => {
        if ((error?.cause as AxiosError)?.response?.status !== 400) {
          setFormState(FormState.ERROR)
        } else if ((error?.cause as AxiosError<APIErrorResponse>)?.response?.status === 400) {
          const errorBody = (error?.cause as AxiosError<APIErrorResponse>)?.response?.data?.error

          if (isInvalidCaptchaTokenError(error)) {
            setMustReverifyCaptcha(true)
          } else {
            setServerError(errorBody?.message || t('errorMessage.somethingWentWrong'))
          }
        }
      }
    }
  )

  const handleNext = () => {
    if (files[0]) {
      setServerError(null)
      setMustReverifyCaptcha(false)
      const formData = new FormData()
      formData.append('file', files[0])
      formData.append('validationInviteId', programId || '')
      formData.append('documentType', DEFAULT_DOCUMENT_TYPE)

      uploadDocument(formData, {
        onSuccess: data => {
          setValue('validationSubmissionDocumentId', data.id, { shouldValidate: true })
          setFormState(FormState.DIRTY)
          handleNextStep(EducationHostedFormSteps.TERMS)
        }
      })
    }
  }

  const handleDrop = (newFiles: FileWithPath[]) => {
    setFiles(newFiles)
    setServerError(null)
    setMustReverifyCaptcha(false)
  }

  return (
    <Step
      step={EducationHostedFormSteps.DOCUMENT_UPLOAD}
      title={t('documentUpload.title')}
      description={t(descriptionKey)}
      onNext={handleNext}
      nextButtonDisabled={files.length === 0}
      nextButtonLabel={t('button.next')}
      progress={progress}
      isLoading={isPending}
    >
      <Flex direction="column" gap={16} w="100%">
        {mustReverifyCaptcha && <Alert variant="warning">{t('errorMessage.captchaFailed')}</Alert>}
        <FormField label={t('documentUpload.typeOfDocument.label')}>
          <Flex direction="column">
            <span className={styles.placeholder}>{t('documentUpload.typeOfDocument.placeholder')}</span>
            <ul className={styles.documentItemList}>
              {documentTypes.map(({ key, recommended }) => (
                <li key={key} className={styles.documentItemListItem}>
                  <Trans i18nKey={key} components={{ b: <strong /> }} />
                  {recommended && (
                    <>
                      <Spacer size={2} />
                      <Badge variant="positive" style={{ marginLeft: 8 }}>
                        <Trans i18nKey="documentUpload.typeOfDocument.recommended" components={{ b: <strong /> }} />
                      </Badge>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </Flex>
        </FormField>
        {partnerPrivacyNotice && <Typography>{partnerPrivacyNotice}</Typography>}
        <FormField
          label={t('documentUpload.dropzone.label')}
          status={serverError ? 'danger' : getStatus('validationSubmissionDocumentId')}
          necessity="required"
          statusMessage={serverError || errors.validationSubmissionDocumentId}
        >
          <Dropzone
            title={t('documentUpload.dropzone.title')}
            description={t('documentUpload.dropzone.description')}
            onChange={handleDrop}
            files={files}
            accept={{
              'application/pdf': ['.pdf'],
              // .heic/.heif needed: Chrome and Firefox often report an empty type for HEIC.
              'image/*': ['.png', '.jpg', '.jpeg', '.heic', '.heif']
            }}
            maxSize={5 * 1024 * 1024}
            maxFiles={1}
            testId="document-upload-dropzone"
          />
        </FormField>
      </Flex>
    </Step>
  )
}
